import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePortal } from '../lib/portal';
import { SOLUTIONS } from '../lib/solutions';
import { ThemeToggle } from './ThemeToggle';
import { PageGlow } from './PageGlow';
import { GLOW_ENABLED } from '../lib/siteEffects';
import { CONTACT } from '../lib/contact';
import '../styles/public.css';

/**
 * The header shared by every public/marketing page (landing, Solutions, About,
 * Request a demo). One header means the navigation, the Solutions menu and the
 * theme toggle are identical wherever a visitor lands.
 *
 * Every destination is a pill — a bordered chip carrying an icon and its label.
 * Bare text links gave the eye nothing to aim at and no sense of which page it
 * was already on; a pill has an edge, so it reads as a target, and the page you
 * are on can hold a filled one.
 *
 * Two behaviours are worth knowing about before changing anything here:
 *
 * 1. The Solutions menu is a click-toggled dropdown. Hover alone excludes touch
 *    and keyboard users. It closes on outside click and on Escape, and its
 *    items are real links so the menu works even mid-JavaScript-load.
 *
 * 2. Below 1400px the whole navigation collapses behind a Menu button. That is
 *    a width, not a phone: seven icon-and-label pills plus the theme toggle and
 *    two buttons genuinely do not fit a laptop, and letting them wrap put a
 *    second row of navigation over the hero. It also repairs a real hole —
 *    the previous rule was `display: none` on every nav link under 768px, so a
 *    visitor on a phone could reach Solutions, Blog, Pricing and About from
 *    nowhere at all.
 */

/* Line icons, same language as the marketing cards: one stroke glyph, currentColor,
   sized by the pill rather than by a hard-coded box. */
const svg = (children: ReactNode) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{children}</svg>
);

const ICONS: Record<string, ReactNode> = {
  home: svg(<><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V20h13V9.5" /></>),
  solutions: svg(<><rect x="3" y="3" width="7.5" height="7.5" rx="1.6" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" /></>),
  homes: svg(<><circle cx="11" cy="11" r="7" /><path d="M16.2 16.2 21 21" /><path d="M8.4 11.3 11 9.2l2.6 2.1" /><path d="M9.3 10.6v3.2h3.4v-3.2" /></>),
  blog: svg(<><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z" /><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5z" /></>),
  pricing: svg(<><path d="M3.5 11.4V4.5A1 1 0 0 1 4.5 3.5h6.9a1 1 0 0 1 .7.3l8.1 8.1a1 1 0 0 1 0 1.4l-6.9 6.9a1 1 0 0 1-1.4 0L3.8 12.1a1 1 0 0 1-.3-.7z" /><circle cx="8" cy="8" r="1.4" /></>),
  about: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 11v5.5" /><circle cx="12" cy="7.8" r=".9" fill="currentColor" stroke="none" /></>),
  demo: svg(<><rect x="2.5" y="4" width="19" height="13" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /><path d="M10.6 8.4 14.4 10.5l-3.8 2.1z" /></>),
  signin: svg(<><path d="M15 3h3.5A1.5 1.5 0 0 1 20 4.5v15a1.5 1.5 0 0 1-1.5 1.5H15" /><path d="M10 16.5 14.5 12 10 7.5" /><path d="M14.5 12H3.5" /></>),
  app: svg(<><path d="M4 13a8 8 0 0 1 16 0" /><path d="M4 13h16" /><path d="M12 13l3.5-3.8" /></>),
  phone: svg(<path d="M6.6 10.8a15 15 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A16 16 0 0 1 3 5c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1z" />),
};

export function PublicHeader() {
  const [open, setOpen] = useState(false);      // Solutions dropdown
  const [navOpen, setNavOpen] = useState(false); // collapsed navigation
  const wrap = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  // Signed in, the two right-hand buttons invited someone to do what they had
  // already done — and worse, the only route back into the app from a public
  // page was the address bar. One button, pointing at whichever portal this
  // person actually works in, replaces both.
  const { isAuthenticated, appHome, appLabel, siteHome } = usePortal();

  // Close both on any navigation.
  useEffect(() => { setOpen(false); setNavOpen(false); }, [pathname]);

  // Close on outside click or Escape. Escape closes the dropdown first and the
  // collapsed navigation second, so one key does not dismiss two layers at once.
  useEffect(() => {
    if (!open && !navOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (open) setOpen(false);
      else setNavOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, navOpen]);

  /*
   * The page you are on gets a filled pill. Matching is on the path alone and
   * only where a page owns one: Pricing is a #hash on the home page, so
   * treating it as a destination would light two pills at once on "/" and tell
   * the visitor something untrue about where they are.
   */
  const here = (path?: string) => (path && pathname === path ? 'page' : undefined);

  const pill = (to: string, icon: string, label: string, match?: string) => (
    <Link className="ph-pill" to={to} aria-current={here(match)}>
      {ICONS[icon]}<span>{label}</span>
    </Link>
  );

  return (
    <>
      {/* The cursor glow rides behind every public page, rendered here (once,
          from the shared header) rather than page by page. It is a sibling of
          the header, not a child: the header is sticky with its own stacking
          context, and a fixed layer nested inside it would be trapped there. */}
      {GLOW_ENABLED && <PageGlow />}
      <header className="lp-nav">
      <Link to={siteHome} className="lp-brand">
        <img src="/brand-mark-64.png?v=2" alt="" width={28} height={28} className="brand-mark" />
        NyumbaSuite
      </Link>

      <button
        type="button"
        className="ph-pill ph-burger"
        aria-expanded={navOpen}
        aria-controls="ph-primary"
        onClick={() => setNavOpen((o) => !o)}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          {navOpen
            ? <><path d="M6 6l12 12" /><path d="M18 6 6 18" /></>
            : <><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></>}
        </svg>
        <span>{navOpen ? 'Close' : 'Menu'}</span>
      </button>

      <nav id="ph-primary" className={`lp-nav-links${navOpen ? ' open' : ''}`} aria-label="Primary">
        {pill(siteHome, 'home', 'Home', siteHome)}

        {/* Find a home leads, first after Home and ahead of Solutions: the
            tenant's errand is the one this landlord-messaged site otherwise
            hides, so it earns the most prominent slot. It names the errand
            ("Find a home") rather than the section, points at /homes with the
            URL unchanged, and the tabs inside still switch to Services and
            Marketplace. */}
        {pill('/homes', 'homes', 'Find a home', '/homes')}

        <div className="ph-menu" ref={wrap}>
          <button
            type="button"
            className="ph-pill ph-menu-btn"
            aria-haspopup="true"
            aria-expanded={open}
            aria-current={here('/solutions')}
            onClick={() => setOpen((o) => !o)}
          >
            {ICONS.solutions}<span>Solutions</span>
            <span className={`ph-caret${open ? ' up' : ''}`} aria-hidden>▾</span>
          </button>
          {open && (
            <div className="ph-mega" role="menu">
              <div className="ph-mega-list">
                {SOLUTIONS.map((s) => (
                  <Link key={s.id} to={`/solutions#${s.id}`} className="ph-mega-item" role="menuitem"
                    onClick={() => setOpen(false)}>
                    <span className="ph-mega-ic" aria-hidden>{s.icon}</span>
                    <span className="ph-mega-text">
                      <span className="ph-mega-name">
                        {s.name}
                        {s.available === false && <span className="ph-soon">Soon</span>}
                      </span>
                      <span className="ph-mega-tag">{s.tagline}</span>
                    </span>
                    <span className="ph-mega-chev" aria-hidden>›</span>
                  </Link>
                ))}
              </div>
              <div className="ph-mega-promo">
                <span className="ph-mega-promo-ic" aria-hidden>
                  <img src="/brand-mark-64.png?v=2" alt="" width={30} height={30} />
                </span>
                <h3 className="ph-mega-promo-title">Property management that thinks ahead</h3>
                <p className="ph-mega-promo-text">
                  One platform for landlords and property managers across Kenya and East Africa —
                  rent, tenants, maintenance and reports, with AI that watches your portfolio
                  alongside you.
                </p>
                <Link className="btn btn-primary btn-sm" to="/solutions" onClick={() => setOpen(false)}>
                  See all solutions
                </Link>
              </div>
            </div>
          )}
        </div>

        {pill(`${siteHome}#pricing`, 'pricing', 'Pricing')}
        {pill('/about', 'about', 'About', '/about')}
        {/* Blog sits after About, near the end: it is content marketing, a
            secondary destination, so the primary run of the nav leads with the
            product (Find a home, Solutions, Pricing) and keeps Blog out of it. */}
        {pill('/blog', 'blog', 'Blog', '/blog')}
        {pill('/demo', 'demo', 'Request a demo', '/demo')}

        {/* Subtle, icon-only — a click-to-call, not a "Contact Us" nav tab.
            The brief is explicit that adding a labelled tab is not wanted;
            this is the header's own answer to "how do I reach someone" for
            a visitor who never scrolls to the footer or the homepage band. */}
        <a className="ph-pill ph-contact" href={CONTACT.primaryTel} aria-label="Call NyumbaSuite">
          {ICONS.phone}
        </a>

        <ThemeToggle />

        {isAuthenticated ? (
          <Link className="btn btn-primary btn-sm ph-cta" to={appHome}>
            {ICONS.app}<span>{appLabel}</span>
          </Link>
        ) : (
          <>
            <Link className="ph-pill ph-pill-accent" to="/login">
              {ICONS.signin}<span>Sign in</span>
            </Link>
            <Link className="btn btn-primary btn-sm ph-cta" to="/register">Get started</Link>
          </>
        )}
      </nav>
      </header>
    </>
  );
}
